import React, { useState, useEffect } from 'react';
import { FieldError } from 'react-hook-form';

type Props = {
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  label?: string;
  placeholder?: string;
  type: string;
  name?: string;
  id?: string;
  defaultValue?: string;
  value?: string | number;
  register?: Function;
  validation?: object;
  error?: any;
  errorText?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  iconUrl?: string;
  disabled?: boolean;
  maxLength?: number;
  optional?: boolean;
  min?: number;
  max?: number;
  step?: number | string;
};

const Input = ({
  className = '',
  inputClassName = '',
  labelClassName = '',
  label = '',
  placeholder = '',
  type,
  id = '',
  name = '',
  defaultValue = '',
  value,
  register,
  onChange: customOnChange = () => { },
  onBlur,
  validation,
  maxLength,
  error,
  errorText = 'Please enter a value',
  iconUrl = '',
  disabled = false,
  optional = false,
  min,
  max,
  step
}: Props) => {
  const [isErrorVisible, setIsErrorVisible] = useState(!!error);

  useEffect(() => {
    setIsErrorVisible(!!error);
  }, [error]);

  // SAFELY register the input if provided
  let fieldProps = {};
  let ref: any = null;
  let formOnChange: ((e: React.ChangeEvent<HTMLInputElement>) => void) | undefined;

  if (register && typeof register === 'function' && name) {
    const result = register(name, validation);
    if (result) {
      ({ ref, onChange: formOnChange, ...fieldProps } = result);
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isErrorVisible) setIsErrorVisible(false);
    formOnChange?.(event);     // react-hook-form's change
    let newValue = event.target.value;

    // Only apply for number inputs
    if (type === 'number') {
      const numeric = Number(newValue);
      if (min !== undefined && numeric < min) {
        newValue = min.toString();
      } else if (max !== undefined && numeric > max) {
        newValue = max.toString();
      }
      // Remove this line:
      // event.target.value = newValue;
    }

    customOnChange?.(event);
  };

  return (
    <div className={`grid grid-cols-1 mb-5 relative ${className}`}>
      {label && (
        <label className={`text-xs text-[var(--text-muted)] mb-1 ${labelClassName}`}>
          {label}
          {optional && (
            <span className="ml-1 text-xs text-[var(--text-muted)]">(optional)</span>
          )}
        </label>
      )}
      <div className="relative grid grid-cols-1">
        {iconUrl && (
          <img
            src={iconUrl}
            alt="start"
            className="theme-icon pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 opacity-60"
          />
        )}
        <input
          ref={ref}
          onChange={handleChange}
          onBlur={onBlur}
          {...fieldProps}
          className={`h-12 rounded-lg border border-[var(--border-light)] bg-[var(--bg-surface)] px-4 text-base text-[var(--text-main)] shadow-sm outline-none transition-colors placeholder:text-[var(--text-muted)] focus:ring-0 ${inputClassName} ${iconUrl ? 'ps-11' : ''
            } ${isErrorVisible ? 'border !border-danger' : ''} ${disabled ? 'cursor-no-drop opacity-70' : ''
            }`}
          type={type}
          id={id}
          name={name}
          placeholder={placeholder}
          // defaultValue={defaultValue}
          // value={value}
          {...(value !== undefined
            ? { value }
            : { defaultValue })}
          disabled={disabled}
          maxLength={maxLength}
          min={min}
          max={max}
          step={step}
        />
      </div>
      {isErrorVisible && (
        <p className="mt-1 text-xs text-[var(--color-error)]">{errorText}</p>
      )}
    </div>
  );
};

export default Input;
